import { UISchemaElement } from "@jsonforms/core";

export interface MappedUiSchemaElement extends UISchemaElement {
	label?: any;
	depth?: number;
	headingLevel?: number;
	elements?: MappedUiSchemaElement[];
	options?: any;
}

/**
 * Adding depth and headingLevel to detail section of control type.
 */
const mapDetailSection = (
	detail: MappedUiSchemaElement | undefined,
	depth: number,
	headingLevel: number
): MappedUiSchemaElement | undefined => {
	if (!detail) return;

	const elements = Array.isArray(detail.elements)
		? (detail.elements
			.map((el) => mapUiSchemaWithDepth(el, depth + 1, headingLevel))
			.filter(Boolean) as MappedUiSchemaElement[])
		: detail.elements;

	return {
		...detail,
		depth,
		headingLevel,
		elements,
	};
};

/**
 * Adding depth and headingLevel metadata to uischema.
 * Increase headingLevel for children element only if -
 * - the parent element has label to show as heading
 * - the type is not Categorization or Category (as we dont use headings to show category names)
 */
export const mapUiSchemaWithDepth = (
	element?: MappedUiSchemaElement,
	depth = 0,
	headingLevel = 2
): MappedUiSchemaElement | undefined => {
	if (!element) return;

	const hasLabel = element.label;
	const shouldIncreaseHeading = hasLabel && !["Category", "Categorization"].includes(element.type);
	const nextHeadingLevel = shouldIncreaseHeading ? headingLevel + 1 : headingLevel;

	const mapped: MappedUiSchemaElement = {
		...element,
		depth,
		headingLevel,
	};

	const mapChildren = (children?: MappedUiSchemaElement[]) =>
		children
			?.map((child) => mapUiSchemaWithDepth(child, depth + 1, nextHeadingLevel))
			.filter(Boolean) as MappedUiSchemaElement[];

	if (Array.isArray(element.elements)) {
		mapped.elements = mapChildren(element.elements);
		return mapped;
	}

	if (element.type === "Control" && element.options) {
		return {
			...mapped,
			options: {
				...element.options,
				depth: depth + 1,
				headingLevel: nextHeadingLevel,
				detail: mapDetailSection(element.options.detail, depth + 1, nextHeadingLevel),
			},
		};
	}

	return mapped;
};

export default mapUiSchemaWithDepth;
